import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const LANES = ["NOT STARTED", "IN PROGRESS", "REVIEW", "DONE"];

const MiniKanban = () => (
  <div className="grid grid-cols-4 gap-1.5">
    {LANES.map((lane, i) => (
      <div key={lane} className="rounded-lg border border-white/10 bg-black/40 p-1.5">
        <p className="mb-1.5 text-[7px] font-bold uppercase tracking-wider text-white/35">{lane}</p>
        {Array.from({ length: [2, 3, 1, 2][i] }).map((_, j) => (
          <div key={j} className="mb-1 rounded bg-white/[0.07] px-1.5 py-1">
            <span className="block h-1 w-full rounded bg-white/25" />
            <span className="mt-1 block h-1 w-2/3 rounded bg-white/15" />
          </div>
        ))}
      </div>
    ))}
  </div>
);

const MiniTable = () => (
  <div className="space-y-1">
    {["3D MODEL - PIPING · UNIT 1", "ORTHO DRAWINGS · UNIT 2", "TIE-IN LIST · GENERAL"].map((row, i) => (
      <div key={row} className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-2 py-1.5">
        <span className="flex-1 truncate text-[8px] text-white/55">{row}</span>
        <span className="h-1 w-10 rounded-full bg-white/10">
          <span className="block h-1 rounded-full bg-[#00FF66]" style={{ width: `${[30, 65, 100][i]}%` }} />
        </span>
        <span className="text-[7px] font-bold text-white/35">{[30, 65, 100][i]}%</span>
      </div>
    ))}
  </div>
);

const MiniCalendar = () => (
  <div className="grid grid-cols-7 gap-1">
    {Array.from({ length: 21 }).map((_, i) => (
      <div
        key={i}
        className={`flex h-6 items-center justify-center rounded text-[7px] ${
          [8, 9, 10].includes(i)
            ? "bg-[#00FF66]/20 text-[#00FF66]"
            : i === 15
            ? "bg-[#FF8B94]/20 text-[#FF8B94]"
            : "bg-white/[0.04] text-white/25"
        }`}
      >
        {i + 1}
      </div>
    ))}
  </div>
);

const MiniMindmap = () => (
  <div className="relative h-24">
    {[
      { t: "Vendor data late?", c: "#FFD166", x: 2, y: 4, r: -3 },
      { t: "Freeze model wk 6", c: "#00FF66", x: 42, y: 30, r: 2 },
      { t: "Shutdown window", c: "#FF8B94", x: 60, y: 2, r: -1 },
    ].map((n) => (
      <div
        key={n.t}
        className="absolute w-[34%] rounded-md p-1.5 text-[7px] font-semibold text-black"
        style={{ left: `${n.x}%`, top: `${n.y}%`, background: n.c, transform: `rotate(${n.r}deg)` }}
      >
        {n.t}
      </div>
    ))}
  </div>
);

const MiniGantt = () => (
  <div className="space-y-2">
    {[
      { label: "3D model freeze", left: 0, width: 38 },
      { label: "Ortho drawings", left: 40, width: 30 },
      { label: "As-built package", left: 72, width: 26 },
    ].map((bar, i) => (
      <div key={bar.label} className="flex items-center gap-2">
        <span className="w-20 shrink-0 truncate text-[7px] text-white/40">{bar.label}</span>
        <div className="relative h-2.5 flex-1 rounded bg-white/[0.04]">
          <div
            className="absolute h-2.5 rounded bg-[#00FF66]/70"
            style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
          />
          {i > 0 && (
            <span
              className="absolute -top-1.5 h-1.5 w-2 border-l border-t border-[#00FF66]/50"
              style={{ left: `${bar.left - 2}%` }}
            />
          )}
        </div>
      </div>
    ))}
  </div>
);

const MiniAudit = () => (
  <div className="space-y-1 font-mono">
    {[
      ["09:14", "Admin", "3D MODEL - PIPING", "NOT STARTED → IN PROGRESS"],
      ["11:02", "M. Camacho", "ORTHO DRAWINGS", "progress 40% → 65%"],
      ["16:37", "Admin", "TIE-IN LIST", "due date changed"],
    ].map((row) => (
      <div key={row[0]} className="flex gap-2 rounded border border-white/[0.07] bg-black/50 px-2 py-1 text-[7px]">
        <span className="text-white/30">{row[0]}</span>
        <span className="w-16 shrink-0 truncate text-white/60">{row[1]}</span>
        <span className="flex-1 truncate text-white/40">{row[2]}</span>
        <span className="hidden text-[#00FF66]/70 sm:block">{row[3]}</span>
      </div>
    ))}
  </div>
);

const MiniReport = () => (
  <div className="rounded-lg bg-white p-2.5">
    <div className="mb-1.5 flex items-center justify-between">
      <span className="text-[7px] font-black tracking-widest text-black">VEKTOR · PLANT EXPANSION</span>
      <span className="text-[6px] text-black/40">weekly status</span>
    </div>
    {[70, 45, 90].map((w, i) => (
      <div key={i} className="mb-1 flex items-center gap-1.5">
        <span className="h-1 flex-1 rounded bg-black/10">
          <span className="block h-1 rounded bg-black/70" style={{ width: `${w}%` }} />
        </span>
        <span className="text-[6px] text-black/50">{w}%</span>
      </div>
    ))}
  </div>
);

const MOCKUPS = {
  kanban: MiniKanban,
  table: MiniTable,
  calendar: MiniCalendar,
  mindmap: MiniMindmap,
  gantt: MiniGantt,
  audit: MiniAudit,
  report: MiniReport,
};

export const PLAN_EXAMPLES = {
  free: {
    tagline: "Enough to run one small revamp, on your own.",
    scenarios: [
      {
        title: "Solo drafter, one revamp",
        body: "You're tracking 20 drawings for a single unit revamp. One project and a board are all you need to see what's stuck in REVIEW.",
      },
      {
        title: "Trying it with two colleagues",
        body: "Three logins, no card. Prove the workflow on a live job before you ask anyone to approve a budget.",
      },
    ],
    features: [
      { name: "6-lane Kanban board", copy: "Drag a drawing from NOT STARTED to REVIEW; everyone sees it instantly.", mockup: "kanban" },
      { name: "Task table with filters", copy: "Filter by area, unit, discipline or owner and update progress inline.", mockup: "table" },
    ],
  },
  starter: {
    tagline: "For a small discipline crew on a real job.",
    scenarios: [
      {
        title: "5-person piping crew, plant expansion",
        body: "Unlimited projects means one per unit — PLX-U1, PLX-U2, tie-ins. Isometrics tracked by area, owners assigned, progress rolled up on the dashboard.",
      },
      {
        title: "Planning a shutdown window",
        body: "The Time Off calendar shows two of your modellers are on vacation in week 6, so you move the model freeze before you commit to a date.",
      },
      {
        title: "Scope capture before kickoff",
        body: "Dump every unknown onto the Mind map — vendor data, missing P&IDs, field verification — then convert the real ones into tasks in one click.",
      },
    ],
    features: [
      { name: "Time Off calendar", copy: "Vacation, sick and personal days with holidays, approvals and coverage at a glance.", mockup: "calendar" },
      { name: "Private Mind map", copy: "Sticky notes only you can see, colour-coded, convertible into tracked tasks.", mockup: "mindmap" },
    ],
  },
  team: {
    tagline: "For multidiscipline work with a hard turnover date.",
    scenarios: [
      {
        title: "12-person team, fixed turnover",
        body: "Ortho drawings can't start before the 3D model freeze. Set a finish-to-start dependency and the Timeline shows exactly what slips when the model does.",
      },
      {
        title: "Vendor data arrives two weeks late",
        body: "Drag the model-freeze bar two weeks right; dependent tasks and the overdue watchlist update, so you know the turnover impact the same morning.",
      },
      {
        title: "Friday report to the PM",
        body: "Pick the project, hit print, send a clean PDF: progress by discipline, workload per person, everything overdue, who's off next week.",
      },
      {
        title: "Lead sign-off before IFC",
        body: "Send each isometric to the piping lead for review. They approve or request changes straight from their To Review queue, and every review cycle and day in review is counted.",
      },
      {
        title: "A delivery date nobody can miss",
        body: "Post a Critical announcement for the 10% package delivery. It pops up for everyone, stays pinned as a countdown banner, and you can see who hasn't acknowledged it and remind them in one click.",
      },
      {
        title: "The client PM wants to watch",
        body: "Add them as a Viewer: read-only access to the board, timeline and reports, no editing. On Team each Viewer counts as a paid seat.",
      },
    ],
    features: [
      { name: "Timeline / Gantt with dependencies", copy: "Real start and due dates, drag to reschedule, finish-to-start arrows between tasks.", mockup: "gantt" },
      { name: "Printable status report", copy: "Client-ready black-on-white report — no spreadsheet assembly on a Friday afternoon.", mockup: "report" },
    ],
  },
  business: {
    tagline: "For EPC work that gets audited.",
    scenarios: [
      {
        title: "Client document-control audit",
        body: "Every status change, note edit and due-date move is on the record with who and when. Export the range to CSV and hand it over.",
      },
      {
        title: "Subcontractors on the same job",
        body: "Unlimited members, so field crews and subs get their own logins with member-level permissions — they update their own scope, nothing else.",
      },
      {
        title: "Retention on your terms",
        body: "Keep 12 months of history, then export and clear older entries yourself instead of hoping a vendor kept it.",
      },
      {
        title: "Management eyes, at no extra cost",
        body: "Give directors, client PMs and stakeholders Viewer access to every project. On Business, Viewer seats are free, so oversight never adds to the bill.",
      },
    ],
    features: [
      { name: "Admin audit log + CSV export", copy: "A filterable ledger of every change, by person, project or date range.", mockup: "audit" },
      { name: "Unlimited free Viewer seats", copy: "Directors, client PMs and stakeholders get read-only access to every project at no extra cost.", mockup: "kanban" },
    ],
  },
};

export default function PlanExamplesDialog({ tier, onClose }) {
  useEffect(() => {
    if (!tier) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [tier, onClose]);

  const data = tier ? PLAN_EXAMPLES[tier.id] : null;

  return (
    <AnimatePresence>
      {tier && data && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:items-center sm:p-8"
          data-testid="plan-examples-dialog"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.45, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-[#0d0f12] p-7 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)] sm:p-10"
          >
            <span className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#00FF66]/10 blur-3xl" aria-hidden="true" />
            <button
              onClick={onClose}
              className="absolute right-5 top-5 rounded-full border border-white/10 p-2 text-white/40 transition-colors duration-200 hover:border-white/25 hover:text-white"
              data-testid="plan-examples-close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[#00FF66]">
              How {tier.name} works
            </p>
            <h3 className="font-display max-w-lg text-3xl font-black tracking-tighter sm:text-4xl">
              {data.tagline}
            </h3>
            <p className="mt-4 text-sm text-white/40">
              {tier.id === "free" ? "Free forever" : `$${tier.monthly} per seat / month · 7-day free trial, no card`}
            </p>

            <div className="mt-9 space-y-4">
              {data.scenarios.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.09, ease: EASE }}
                  className="border-l border-[#00FF66]/30 pl-5"
                >
                  <p className="flex items-center gap-2 text-sm font-bold text-white">
                    <Check className="h-3.5 w-3.5 shrink-0 text-[#00FF66]" />
                    {s.title}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/50">{s.body}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {data.features.map((f, i) => {
                const Mockup = MOCKUPS[f.mockup];
                return (
                  <motion.div
                    key={f.name}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: 0.3 + i * 0.1, ease: EASE }}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
                  >
                    <div className="mb-3.5 overflow-hidden rounded-xl border border-white/[0.07] bg-black/30 p-2.5">
                      <Mockup />
                    </div>
                    <p className="text-xs font-bold uppercase tracking-widest text-[#00FF66]">{f.name}</p>
                    <p className="mt-2 text-xs leading-relaxed text-white/45">{f.copy}</p>
                  </motion.div>
                );
              })}
            </div>

            <a
              href={`/signup?plan=${tier.id}`}
              className="mt-9 inline-block rounded-full bg-[#00FF66] px-7 py-3.5 text-sm font-bold text-black shadow-[0_0_30px_rgba(0,255,102,0.3)] transition-transform duration-200 hover:scale-[1.03]"
              data-testid="plan-examples-cta"
            >
              {tier.id === "free" ? "Start free" : `Start your ${tier.name} trial`}
            </a>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
